import Vue from "vue";
import deepmerge from "deepmerge";
import set from "lodash/set";
import get from "lodash/get";
import {
  _isMobile,
  nombreMotorTagging,
  getEnvironment,
  campania
} from "@/utils";

const AdobeFunctions = {
  internal(context, _callback) {
    AdobeFunctions.clearVars();
    AdobeFunctions.setConstants();
    AdobeFunctions.setOptimizations(context);
    if (campania === "ofertaAutomotor") {
      AdobeFunctions.setUserVars(context);
    }
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event) {
    /**
     * Realizar llamada al DTM
     */
    try {
      window[method](event);
      // Resetear flag de pageview automático
      window._pageViewAutomatico = false;
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "internalCampaign.attributes", []);
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(window.digitalData, "application.errorType", "");
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(window.digitalData, "page.pageActivity.link.name", "");
    set(window.digitalData, "application.typology", "");
    set(window.digitalData, "application.process", "");
    set(window.digitalData, "page.pageInfo.pageIntent", "");
    set(window.digitalData, "user.segment.profile", "");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: ""
        },
        fulfillmentModel: "",
        globalApplication: ""
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    const enviroment = getEnvironment();
    if (enviroment !== null) {
      set(window.digitalData, "pageInstanceID", enviroment.name);
      return;
    }
    set(window.digitalData, "pageInstanceID", "pro");
  },
  setIsMobile() {
    if (_isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (_isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      "general",
      nombreMotorTagging()
    ];
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level2", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
    AdobeFunctions.setPageInstanceId();
    AdobeFunctions.setIsMobile();
    AdobeFunctions.setSysEnv();
  },
  setOptimizations(context) {
    if (context?.state?.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej: "AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: ""
          }
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setInternalCampaign() {
    let attributes = [
      {
        location: "",
        campaignFormat: "",
        collectiveCode: "",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: ""
      }
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setCommonProductAttributes(amount, state) {
    let productObject = {
      primaryCategory: "seguros",
      productSubtype:
        campania === "ofertaAutomotor" ? "seguros auto" : "seguros hogar",
      productName: campania === "ofertaAutomotor" ? "seguro automotor" : "hogar"
    };

    if (amount && amount != "mostrarState") {
      AdobeFunctions.setProductAttributes({
        productAttributes: [
          {
            ...productObject,
            currency: "ARS",
            amount: amount
          }
        ]
      });
    }
    if (amount === undefined) {
      AdobeFunctions.setProductAttributes({
        productAttributes: [
          {
            ...productObject
          }
        ]
      });
    }
    if (amount && amount === "mostrarState") {
      AdobeFunctions.setProductAttributes({
        productAttributes: [
          {
            ...productObject,
            state: state,
            currency: "ARS"
          }
        ]
      });
    }
  },
  setProductAttributes(oferta) {
    let attributes = [];
    let attributesModel = {
      primaryCategory: "",
      productSubtype: "",
      productName: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: ""
      },
      term: "",
      group: "",
      state: ""
    };

    let customProductAttributes = get(oferta, "productAttributes", null);

    if (customProductAttributes) {
      customProductAttributes.forEach(attr => {
        attributes.push(deepmerge(attributesModel, attr));
      });
    }

    set(window.digitalData, "products.attributes", attributes);
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps(type, name, globalApplication) {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: type,
          name: name
        },
        fulfillmentModel: "online"
      })
    );
    set(window.digitalData, "application.globalApplication", globalApplication);
  },
  tienePatente(context) {
    if (get(context, "state.oferta.plazo", "") != null) {
      set(window.digitalData, "user.segment.profile", "usuario con patente");
    } else {
      set(window.digitalData, "user.segment.profile", "usuario sin patente");
    }
  },
  setUserVars(context) {
    set(
      window.digitalData,
      "user.prospectID",
      get(context, "state.userIdTokenizado", "")
    );
  }
};
export { AdobeFunctions };
